import React from 'react'
import DataTable from 'react-data-table-component'
import {FaRedo} from 'react-icons/fa'
import {FiInbox} from 'react-icons/fi'
import {ReadingModel} from '../../../../entities/entities'
import utilities from '../../../../utitlities/utility'
import Loader from '../Loader'
import {formatToAppTimezone, getBPSeverityLabel} from '../../helper/helperFunction'

interface HealthCriticalityAlertsTableProps {
  healthAlerts: ReadingModel[]
  title: string
  onReload?: () => void
  hidePatientName?: boolean
  showHeader?: boolean
  loading?: boolean
  sortByFilter: string
  handleSortByFilter: (e: React.ChangeEvent<HTMLSelectElement>) => void
  handleReviewToggle?: (id?: any) => void
  loadingRowId?: number | null
}

const HealthCriticalityAlertsTable: React.FC<HealthCriticalityAlertsTableProps> = ({
  healthAlerts,
  title,
  onReload,
  hidePatientName = false,
  showHeader = true,
  loading = false,
  sortByFilter,
  handleSortByFilter,
  handleReviewToggle,
  loadingRowId,
}) => {
  // Generate the notification message
  const generateNotificationMessage = (alert: ReadingModel): string => {
    let message = `Blood Pressure Reading: ${alert?.sys}/${alert?.dia} mmHg`
    if (alert?.pulse) message += `, Pulse: ${alert?.pulse} bpm`
    return message
  }

  // Color‐coded badge
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getSeverityBadge = (alert_category: any) => {
    const severityLabel = getBPSeverityLabel(alert_category)
    const color = utilities.getSeverityColor(alert_category)
    return (
      <span
        className='inline-block px-2 py-1 rounded-full text-xs font-bold'
        style={{backgroundColor: color, color: 'white'}}
      >
        {severityLabel}
      </span>
    )
  }

  // Table columns
  const tableHeaders = [
    ...(!hidePatientName
      ? [
          {
            name: 'Patient Full Name',
            selector: (row: any) => row?.Patient?.full_name || '-',
            sortable: true,
            wrap: true,
          },
        ]
      : []),
    {
      name: 'Notification Message',
      selector: (row: any) => generateNotificationMessage(row),
      cell: (row: any) => {
        const msg = generateNotificationMessage(row)
        return (
          <div title={msg} className='max-w-xs truncate text-left'>
            {msg}
          </div>
        )
      },
      wrap: true,
      grow: 2,
    },
    {
      name: 'Severity Level',
      cell: (row: any) => (
        <span
          className='inline-block px-3 py-1 rounded-2 text-xs fw-bold text-nowrap'
          style={{
            border: `2px solid ${utilities.getSeverityColor(row?.alert_category)}`,
            // borderColor: utilities.getSeverityColor(row.data?.severity),
            color: utilities.getSeverityColor(row?.alert_category),
            // optional: a light background tint
            backgroundColor: `${utilities.getSeverityColor(row?.alert_category)}44`,
          }}
        >
          {getBPSeverityLabel(row?.alert_category)}
        </span>
      ),
      grow: 1.7,
      wrap: true,
    },
    // {
    //   name: 'Status',
    //   cell: (row: any) => {
    //     const isLoading = loadingRowId === row?.id
        
    //     return (
    //       <>
    //         {row?.read_at == 1 ? (
    //           <PrimaryButton color='primary' className='py-2' disabled={isLoading}>
    //             {isLoading ? (
    //               <span className='d-flex align-items-center'>
    //                 <i className='mdi mdi-loading mdi-spin me-1'></i>
    //                 Reviewed
    //               </span>
    //             ) : (
    //               'Reviewed'
    //             )}
    //           </PrimaryButton>
    //         ) : (
    //           <PrimaryButton 
    //             color='secondary' 
    //             className='py-2' 
    //             onClick={() => handleReviewToggle?.(row?.id)}
    //             disabled={isLoading}
    //           >
    //             {isLoading ? (
    //               <span className='d-flex align-items-center'>
    //                 <i className='mdi mdi-loading mdi-spin me-1'></i>
    //                 Loading...
    //               </span>
    //             ) : (
    //               'Mark as Reviewed'
    //             )}
    //           </PrimaryButton>
    //         )}
    //       </>
    //     )
    //   },
    // },
    {
      name: 'Received At',
      selector: (row: any) => formatToAppTimezone(row?.reading_taken_at),
      sortable: true,
    },
  ]

  return (
    <div className='bg-gray-50 p-6'>
      <div className='bg-white rounded-4 shadow overflow-hidden'>
        {showHeader && (
          <div className='d-flex align-items-center justify-content-between px-6 py-4 border-b'>
            <h2 className='text-xl font-semibold text-indigo-700'>{title}</h2>
            <div className='d-flex align-items-center gap-3'>
              <select
                value={sortByFilter}
                onChange={handleSortByFilter}
                className='border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200'
              >
                <option value='all'>All Severity</option>
                <option value='1'>Low</option>
                <option value='3'>Elevated</option>
                <option value='4'>Hypertension Stage 1</option>
                <option value='5'>Hypertension Stage 2</option>
                <option value='6'>Hypertensive Crisis</option>
              </select>
              {onReload && (
                <button onClick={onReload} className='p-2 rounded hover:bg-gray-200' title='Reload'>
                  <FaRedo className='text-green-600' />
                </button>
              )}
            </div>
          </div>
        )}

        <div className='p-6'>
          {loading ? (
            <div className='flex justify-center py-12'>
              <Loader />
            </div>
          ) : healthAlerts.length === 0 ? (
            <div className='d-flex flex-column align-items-center justify-content-center px-8 py-16 bg-white border border-gray-200 rounded-lg text-gray-600'>
              <FiInbox size={64} className='mb-6 text-gray-300 animate-pulse' />
              <h3 className='text-2xl font-bold mb-2'>No Alerts Found</h3>
              <p className='text-sm text-gray-500 mb-4'>
                You’re all caught up! There are no critical alerts right now.
              </p>
            </div>
          ) : (
            <DataTable
              columns={tableHeaders}
              data={healthAlerts}
              pagination
              highlightOnHover
              responsive
              // striped
              defaultSortAsc={false}
              noHeader
              customStyles={{
                rows: {style: {minHeight: '50px'}},
                headCells: {
                  style: {
                    fontSize: '14px',
                    fontWeight: 600,
                  },
                },
                cells: {
                  style: {textAlign: 'center'},
                },
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default HealthCriticalityAlertsTable
